// src/components/POS/cart/CartSidebar.js
import React, { useState, useMemo, useCallback } from "react";
import { X } from "lucide-react";
import { useCart } from "../../contexts/cartContext";
import { usePOSProductsContext } from "../../contexts/POSProductContext";
import { ShoppingCart } from "lucide-react";
import { useTheme } from "../../../CommonComponents/ThemeProvider";
import CartItemRow from "./CartItemRow";
import EditCartItemModal from "../modals/EditCartItemModal";
import PaymentPanel from "../payment/PaymentPanel";
import CashPaymentPanel from "../payment/CashPaymentPanel";
import PaymentSummary from "../payment/PaymentSummary";
import OrderSummaryModal from "../payment/OrderSummaryModal";
import OrderSuccessModal from "../payment/OrderSuccessModal";
import OrderFailedModal from "../payment/OrderFailedModal";
import ConfirmationModal from "../../../CommonComponents/ConfirmationModal";
import EmptyState from "../../../CommonComponents/EmptyState";
import { formatPrice } from "../../utils/FormatPrice";
import { createOrder } from "../../../../services/orderApi";

export default function CartSidebar({ onCloseSidebar }) {
    // Collapsible state for medium screens
    const [collapsed, setCollapsed] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState("cash");
    const { cart, removeFromCart, clearCart, cartCountHighlight } = useCart();
    const { currentTheme } = useTheme();
    const { updateStockAfterOrder } = usePOSProductsContext();

    const [editItem, setEditItem] = useState(null);
    const [deleteItem, setDeleteItem] = useState(null);
    const [isOrderSummaryOpen, setIsOrderSummaryOpen] = useState(false);
    const [isOrderSuccessOpen, setIsOrderSuccessOpen] = useState(false);
    const [isPaymentErrorOpen, setIsPaymentErrorOpen] = useState(false);
    const [pendingPayment, setPendingPayment] = useState(null);
    const [lastConfirmedOrder, setLastConfirmedOrder] = useState(null);
    const [lastAddedItem, setLastAddedItem] = useState(null);
    const [seatNumber, setSeatNumber] = useState("");
    const [isConfirmingOrder, setIsConfirmingOrder] = useState(false);

    const subtotal = useMemo(
        () => cart.reduce((sum, item) => sum + item.price * item.qty, 0),
        [cart]
    );
    const total = subtotal;

    const handlePlaceOrder = useCallback(
        ({ paymentMethod, amountPaid }) => {
            if (cart.length === 0) return;
            setPendingPayment({
                cart,
                subtotal,
                total,
                paymentMethod,
                cashReceived: amountPaid,
                seat_number: seatNumber.trim() || null,
            });
            setIsOrderSummaryOpen(true);
        },
        [cart, subtotal, total, seatNumber]
    );

    const confirmOrder = useCallback(async () => {
        if (isConfirmingOrder || !pendingPayment) return;
        setIsConfirmingOrder(true);
        try {
            const user = JSON.parse(localStorage.getItem("user"));
            if (!user?.id) throw new Error("User not logged in");

            const mappedCart = pendingPayment.cart.map(item => ({
                product_id: item.productId, // always use productId for consistency
                qty: item.qty,
                price: item.price,
            }));

            const orderData = {
                user_id: user.id,
                cart: mappedCart,
                payment_method: pendingPayment.paymentMethod,
                seat_number: pendingPayment.seat_number,
            };
            const createdOrder = await createOrder(orderData);
            setLastConfirmedOrder(createdOrder);
            updateStockAfterOrder(mappedCart);
            window.dispatchEvent(new CustomEvent("pos-order-created", { detail: createdOrder }));
            clearCart();
            setSeatNumber("");
            setIsOrderSummaryOpen(false);
            setIsOrderSuccessOpen(true);
            setPendingPayment(null);
            setLastAddedItem(null);
        } catch (error) {
            console.error("Order API error:", error);
            setIsOrderSummaryOpen(false);
            setIsPaymentErrorOpen(true);
        } finally {
            setIsConfirmingOrder(false);
        }
    }, [isConfirmingOrder, pendingPayment, clearCart, updateStockAfterOrder]);

    // Handler to close the order success modal and reset lastConfirmedOrder
    const handleOrderSuccessClose = () => {
        setIsOrderSuccessOpen(false);
        setLastConfirmedOrder(null);
    };

    return (
        <>
            <aside
                className="w-full rounded-xl shadow-md bg-white flex flex-col overflow-hidden mb-2"
                style={{ height: 380, minHeight: 280, maxHeight: '90vh' }}
            >
                <div
                    className="px-4 py-2 flex-shrink-0 flex justify-between items-center border-b border-gray-200"
                >
                    <div>
                        <h2 className="text-[15px] font-semibold text-gray-700 tracking-normal">
                            {cart.length > 0
                                ? "Pending Order"
                                : lastConfirmedOrder
                                    ? `Order #${lastConfirmedOrder.order_number}`
                                    : "No Pending Order"}
                        </h2>
                        <p
                            className="text-xs mt-0.5 uppercase tracking-wide text-gray-500"
                        >
                            Ordered Items
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        {/* Close/collapse button always beside cart counter, visible on all screens */}
                        {/* Show close button only on small screens (floating sidebar) or when collapsed on md+ */}
                        {((typeof window !== 'undefined' && window.innerWidth < 768) || collapsed) && (
                            <button
                                className="inline-flex p-1 rounded hover:bg-gray-100 focus:outline-none"
                                onClick={() => {
                                    if (typeof onCloseSidebar === 'function') {
                                        onCloseSidebar();
                                    } else {
                                        setCollapsed((c) => !c);
                                    }
                                }}
                                aria-label={collapsed ? 'Expand cart sidebar' : 'Collapse cart sidebar'}
                                type="button"
                            >
                                <X size={22} />
                            </button>
                        )}
                        <span className="relative inline-block">
                            <ShoppingCart
                                size={24}
                                className={
                                    (currentTheme?.background === 'dark' || (currentTheme?.color && ['#000', '#222', '#333', '#1a1a1a', '#111'].includes(currentTheme.color.toLowerCase())))
                                        ? 'text-white'
                                        : 'text-black'
                                }
                            />
                            {cart.length > 0 && (
                                <span
                                    className={`absolute -top-2 -right-2 md:-top-2 md:-right-2 text-xs font-bold rounded-full px-2 py-0.5 transition-transform text-white ${cartCountHighlight ? "scale-125" : "scale-100"}`}
                                    style={{ backgroundColor: currentTheme?.color || 'var(--color-primary)' }}
                                >
                                    {cart.reduce((sum, i) => sum + i.qty, 0)}
                                </span>
                            )}
                        </span>
                    </div>
                </div>

                {/* Collapsible content: hidden on md if collapsed, always visible on lg+ */}
                <div className="flex-1 overflow-y-auto px-4 py-2 space-y-1" style={{ minHeight:"140px" } }>
                    <div>
                        {cart.length > 0 ? (
                            cart.map(item => (
                                <CartItemRow
                                    key={item.lineId || item.id}
                                    item={item}
                                    formatPrice={formatPrice}
                                    onEdit={() => setEditItem(item)}
                                    onDelete={() => setDeleteItem(item)}
                                    highlightKey={lastAddedItem}
                                />
                            ))
                        ) : (
                            <EmptyState icon={ShoppingCart} message="Cart is empty" />
                        )}
                    </div>
                </div>
                <div
                    className="flex-shrink-0 px-4 py-3 rounded-b-xl border-t border-gray-200 bg-white"
                >
                    <PaymentSummary subtotal={subtotal} total={total} />
                </div>

                {editItem && <EditCartItemModal item={editItem} onClose={() => setEditItem(null)} autoFocus={false} />}
                {deleteItem && (
                    <ConfirmationModal
                        isOpen={!!deleteItem}
                        onClose={() => setDeleteItem(null)}
                        onConfirm={() => {
                            removeFromCart(deleteItem.lineId || deleteItem.id, true);
                            setDeleteItem(null);
                        }}
                        title="Remove Item"
                        message={`Are you sure you want to remove "${deleteItem.name}" from the cart?`}
                        confirmText="Yes, Remove"
                        cancelText="Cancel"
                        type="delete"
                    />
                )}

                {isOrderSummaryOpen && pendingPayment && (
                    <OrderSummaryModal
                        isOpen={isOrderSummaryOpen}
                        onClose={() => {
                            if (!isConfirmingOrder) setIsOrderSummaryOpen(false);
                        }}
                        order={{ ...pendingPayment }}
                        onConfirm={confirmOrder}
                        confirming={isConfirmingOrder}
                    />
                )}


                {isOrderSuccessOpen && lastConfirmedOrder && (
                    <OrderSuccessModal
                        isOpen={isOrderSuccessOpen}
                        onClose={handleOrderSuccessClose}
                        orderNumber={lastConfirmedOrder.order_number}
                    />
                )}

                {isPaymentErrorOpen && (
                    <OrderFailedModal
                        isOpen={isPaymentErrorOpen}
                        onClose={() => setIsPaymentErrorOpen(false)}
                        message="Failed to create order. Please try again."
                    />
                )}
            </aside>

            <div className="mt-0.5">
                <aside className="w-72 rounded-xl shadow-sm bg-white p-2 flex flex-col mx-auto mb-1">
                    <label htmlFor="seat-number" className="block text-[12px] font-semibold text-gray-700 mb-1">
                        Seat Number (Optional)
                    </label>
                    <input
                        id="seat-number"
                        type="text"
                        value={seatNumber}
                        onChange={(e) => setSeatNumber(e.target.value)}
                        placeholder="e.g. 12, A3, Table 5"
                        maxLength={20}
                        className="w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs text-gray-700 outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                    />
                </aside>
                <PaymentPanel
                    total={total}
                    cart={cart}
                    onPay={handlePlaceOrder}
                    paymentMethod={paymentMethod}
                    setPaymentMethod={setPaymentMethod}
                />
                {paymentMethod === "cash" && (
                    <div className="mt-1">
                        <CashPaymentPanel total={total} cart={cart} onPay={handlePlaceOrder} />
                    </div>
                )}
            </div>
        </>
    );
}
